import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, ClipboardList, FileBarChart, Layers, Lock, ShieldCheck, Users } from "lucide-react";
import { EntShell, BtnLink, Kicker } from "@/components/enterprise/Ent";

const PILLARS = [
  { icon: FileBarChart, name: "TEAM IMPACT™", body: "Structured activities with learning objectives, optional reflections, a facilitator guide, and a report built from what happened in the session." },
  { icon: Layers, name: "TEAMVERSE™", body: "Your organization's name, team identities, character names, difficulty, and objectives, applied to proven templates and approved by you before the event." },
  { icon: ClipboardList, name: "MISSION LAB™", body: "A growing library of repeatable programs, each organized around a specific team-development goal." },
];

const OPTIONS = [
  { id: "ready", name: "Ready-to-Play", price: "From $249", cta: "Book now", to: "/enterprise/setup?option=ready",
    items: ["Choose any available Mission Lab program", "Choose participant capacity", "Standard learning objectives", "Participant access link and event code", "Facilitator guide (PDF)", "Team Impact Report after the event"],
    note: "Pay securely by card. Confirmed instantly." },
  { id: "customized", name: "Customized Experience", price: "Quoted after review", cta: "Start customizing", to: "/enterprise/setup?option=customized",
    items: ["Everything in Ready-to-Play", "Organization and department identity", "Brand color on participant screens and reports", "Team and character names", "Extra learning objectives and difficulty level", "Language preference", "Customized facilitator materials"],
    note: "Our team reviews your choices and confirms before your event." },
  { id: "partnership", name: "Custom Enterprise Partnership", price: "By proposal", cta: "Request a proposal", to: "/enterprise/proposal",
    items: ["Custom experience development", "Multiple departments or events", "Recurring program arrangements", "Additional facilitator assistance", "Custom deliverables and reporting, within an agreed scope"],
    note: "For larger organizations and public-sector teams." },
];

const STEPS = [
  "Choose a program and participant capacity.",
  "Add your organization details and, if you like, customize the experience.",
  "Share the join link. Participants join with a first name or nickname.",
  "Run the session using the facilitator guide. Most programs take about 60 minutes.",
  "Download your Team Impact Report and schedule your next program.",
];

const ProgramCard = ({ p }) => {
  const live = p.status === "available";
  return (
    <article className="bg-white border border-[#ddd5c9] rounded-lg p-6 flex flex-col" data-testid={`ent-program-${p.id}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold tracking-wider uppercase text-[#5a524b]">{p.duration_min} min</span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${live ? "bg-[#e7efe4] text-[#24502a]" : "bg-[#f1ece4] text-[#5a524b]"}`}>
          {live ? "Available now" : "In development"}
        </span>
      </div>
      <h3 className="font-display text-2xl mt-3 leading-tight">{p.name}</h3>
      <p className="mt-2 text-[15px] text-[#3b3530]">{p.tagline}</p>
      {p.scenario && <p className="mt-3 text-sm text-[#5a524b] leading-relaxed">{p.scenario}</p>}
      <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Learning objectives">
        {p.objectives.map((o) => (
          <li key={o.id} className="text-xs px-2 py-1 rounded bg-[#f1ece4] text-[#3b3530]">{o.label}</li>
        ))}
      </ul>
      <div className="mt-auto pt-6">
        {live ? (
          <BtnLink to={`/enterprise/setup?program=${p.id}`} className="w-full" data-testid={`ent-book-${p.id}`}>Book this program</BtnLink>
        ) : (
          <BtnLink to={`/enterprise/proposal?program=${p.id}`} variant="outline" className="w-full" data-testid={`ent-interest-${p.id}`}>Ask about early access</BtnLink>
        )}
      </div>
    </article>
  );
};

export default function Enterprise() {
  const [cat, setCat] = useState(null);
  useEffect(() => {
    document.title = "Personalized Enterprise Experiences | WasntMe.Party";
    api.get("/enterprise/catalog").then((r) => setCat(r.data)).catch(() => setCat({ programs: [], capacities: [] }));
  }, []);

  return (
    <EntShell>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-[1.25fr_1fr] gap-14 items-center">
        <div className="ent-rise">
          <Kicker>Personalized Enterprise Experiences</Kicker>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-4" data-testid="ent-hero-title">
            Built around <span className="text-[#7a1010]">your team.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#3b3530] max-w-xl leading-relaxed">
            All-digital team programs that turn colleagues into investigators, decision-makers, and collaborators. Each program comes with
            defined learning objectives, a facilitator guide, optional reflections, and a Team Impact Report based on what was recorded in your session.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <BtnLink to="/enterprise/setup" data-testid="ent-hero-book">Book a Ready-to-Play program</BtnLink>
            <BtnLink to="/enterprise/proposal" variant="outline" data-testid="ent-hero-proposal">Request a proposal</BtnLink>
          </div>
          <p className="mt-6 text-sm text-[#5a524b]">U.S. Marine Corps veteran-owned. No murder or violence required. Professional, fictional scenarios for corporate, government, and education teams.</p>
        </div>
        <aside className="ent-rise [animation-delay:120ms] bg-white border border-[#ddd5c9] rounded-lg p-7 shadow-[0_20px_50px_-30px_rgba(22,19,15,0.45)]" aria-label="What's in a Team Impact Report">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#5a524b]">Team Impact Report</span>
            <span className="text-xs text-[#5a524b]">Sample outline</span>
          </div>
          <ol className="mt-5 space-y-3 text-[15px]">
            {["Organization and event details", "Recorded participation and completion", "Before and after reflections (self-reported)", "Anonymous participant reflections", "Facilitator observations", "Learning objectives addressed", "Suggested follow-up activities"].map((s, i) => (
              <li key={s} className="flex gap-3 items-start border-b border-[#efe9e0] pb-3 last:border-0">
                <span className="font-display text-[#7a1010] w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>{s}
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-[#5a524b]">Reports use only recorded data. No individual scoring, ratings, or performance conclusions.</p>
        </aside>
      </section>

      <section className="border-y border-[#ddd5c9] bg-white" aria-labelledby="pillars-h">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 id="pillars-h" className="sr-only">How it fits together</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {PILLARS.map(({ icon: Icon, name, body }) => (
              <div key={name} data-testid={`ent-pillar-${name.slice(0, 4).toLowerCase()}`}>
                <Icon className="text-[#7a1010]" size={26} aria-hidden="true" />
                <div className="font-display text-2xl mt-4">{name}</div>
                <p className="mt-2 text-[15px] text-[#3b3530] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="options" className="max-w-6xl mx-auto px-6 py-20" aria-labelledby="options-h">
        <Kicker>Three ways to buy</Kicker>
        <h2 id="options-h" className="font-display text-3xl sm:text-4xl mt-3">Pick the level of personalization you need.</h2>
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {OPTIONS.map((o, i) => (
            <article key={o.id} className={`rounded-lg p-7 flex flex-col border ${i === 1 ? "bg-[#16130f] text-white border-[#16130f]" : "bg-white border-[#ddd5c9]"}`} data-testid={`ent-option-${o.id}`}>
              <div className={`text-xs font-semibold tracking-[0.18em] uppercase ${i === 1 ? "text-[#e9c98a]" : "text-[#7a1010]"}`}>Option {i + 1}</div>
              <h3 className="font-display text-2xl mt-2">{o.name}</h3>
              <div className={`mt-1 text-sm ${i === 1 ? "text-[#d9d2c8]" : "text-[#5a524b]"}`}>{o.price}</div>
              <ul className="mt-6 space-y-2.5 text-[15px]">
                {o.items.map((it) => (
                  <li key={it} className="flex gap-2.5"><Check size={17} className={`mt-0.5 shrink-0 ${i === 1 ? "text-[#e9c98a]" : "text-[#7a1010]"}`} aria-hidden="true" />{it}</li>
                ))}
              </ul>
              <p className={`mt-6 text-sm ${i === 1 ? "text-[#d9d2c8]" : "text-[#5a524b]"}`}>{o.note}</p>
              <div className="mt-auto pt-6">
                <BtnLink to={o.to} variant={i === 1 ? "primary" : "outline"} className={`w-full ${i === 1 ? "" : ""}`} data-testid={`ent-option-cta-${o.id}`}>{o.cta}</BtnLink>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="programs" className="bg-[#efe9e0] border-y border-[#ddd5c9]" aria-labelledby="programs-h">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Kicker>Mission Lab™</Kicker>
          <h2 id="programs-h" className="font-display text-3xl sm:text-4xl mt-3">A library of team-development programs.</h2>
          <p className="mt-3 text-[15px] text-[#3b3530] max-w-2xl">Each program includes defined objectives, participant roles, an interactive challenge, facilitator instructions, discussion questions, optional reflections, and a Team Impact Report.</p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ent-program-grid">
            {(cat?.programs || []).map((p) => <ProgramCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14" aria-labelledby="pricing-h">
        <div>
          <Kicker>Participant capacity</Kicker>
          <h2 id="pricing-h" className="font-display text-3xl sm:text-4xl mt-3">Clear limits. Clear prices.</h2>
          <table className="mt-8 w-full text-left text-[15px] bg-white border border-[#ddd5c9] rounded-lg overflow-hidden" data-testid="ent-capacity-table">
            <caption className="sr-only">Ready-to-Play pricing by participant capacity</caption>
            <thead className="bg-[#f1ece4]"><tr><th scope="col" className="px-4 py-3">Package</th><th scope="col" className="px-4 py-3">Participants</th><th scope="col" className="px-4 py-3">Ready-to-Play</th></tr></thead>
            <tbody>
              {(cat?.capacities || []).map((c) => (
                <tr key={c.id} className="border-t border-[#efe9e0]"><td className="px-4 py-3 font-semibold">{c.label}</td><td className="px-4 py-3">{c.people}</td><td className="px-4 py-3">{c.price_label}</td></tr>
              ))}
              <tr className="border-t border-[#efe9e0]"><td className="px-4 py-3 font-semibold">Larger groups</td><td className="px-4 py-3">150+</td><td className="px-4 py-3"><a href="/enterprise/proposal" className="underline">Request a proposal</a></td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <Kicker>How it works</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">From booking to report.</h2>
          <ol className="mt-8 space-y-5">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-4"><span className="font-display text-3xl text-[#7a1010] leading-none w-8">{i + 1}</span><span className="text-[15px] pt-1">{s}</span></li>
            ))}
          </ol>
        </div>
      </section>

      <section id="privacy" className="bg-[#16130f] text-[#f1ece4]" aria-labelledby="privacy-h">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#e9c98a]">Privacy, security &amp; procurement</div>
          <h2 id="privacy-h" className="font-display text-3xl sm:text-4xl mt-3 text-white">Designed for careful organizations.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-[15px] leading-relaxed">
            <div><Users className="text-[#e9c98a]" aria-hidden="true" /><h3 className="font-semibold mt-3 text-white">Minimal participant data</h3><p className="mt-1 text-[#d9d2c8]">Participants join with a first name or nickname. No email address, employee ID, or account is needed.</p></div>
            <div><Lock className="text-[#e9c98a]" aria-hidden="true" /><h3 className="font-semibold mt-3 text-white">Organization-only access</h3><p className="mt-1 text-[#d9d2c8]">Only your organization's administrators can see your events and reports. Reflections are anonymous.</p></div>
            <div><ShieldCheck className="text-[#e9c98a]" aria-hidden="true" /><h3 className="font-semibold mt-3 text-white">Retention and deletion</h3><p className="mt-1 text-[#d9d2c8]">Participant data is kept for 12 months after an event, then deleted. Administrators can delete it sooner at any time.</p></div>
            <div><FileBarChart className="text-[#e9c98a]" aria-hidden="true" /><h3 className="font-semibold mt-3 text-white">Fictional by design</h3><p className="mt-1 text-[#d9d2c8]">Never share classified, confidential, or sensitive information. Programs are fictional learning experiences, not certifications or operational simulations.</p></div>
          </div>
          <p className="mt-10 text-sm text-[#bdb3a6] max-w-3xl">Accessibility: enterprise pages are built for keyboard navigation, screen readers, readable contrast, and mobile devices. A formal Section 508 evaluation has not yet been completed. Need a service description, a quote for a purchase order, or other procurement documents? <a href="/enterprise/proposal" className="underline text-white">Request a proposal</a>.</p>
        </div>
      </section>
    </EntShell>
  );
}
